import React, { useEffect, useMemo, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import SearchIcon from "@material-ui/icons/Search";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import WabaTemplateCreateModal from "../../components/WabaTemplateCreateModal";

import { getTemplateBodyText } from "../../services/wabaTemplates.api";
import { useWabaTemplatesPage } from "./hooks/useWabaTemplatesPage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(1, 2),
    flexWrap: "wrap",
  },
  searchField: { flex: 1, minWidth: 200 },
  filterSelect: { minWidth: 180 },
  chip: { fontSize: "0.75rem", fontWeight: 600 },
  bodyCell: {
    display: "block",
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helpText: {
    display: "block",
    padding: theme.spacing(0, 2, 1),
    color: theme.palette.text.secondary,
  },
  emptyBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

// Colores de chip por estado de Meta (v4 MUI Chip no tiene "success"/"warning").
const STATUS_STYLE = {
  APPROVED: { color: "#1b5e20", background: "#e8f5e9", label: "Aprobada" },
  PENDING: { color: "#e65100", background: "#fff3e0", label: "Pendiente" },
  REJECTED: { color: "#b71c1c", background: "#ffebee", label: "Rechazada" },
  PAUSED: { color: "#616161", background: "#f5f5f5", label: "Pausada" },
  DISABLED: { color: "#616161", background: "#f5f5f5", label: "Deshabilitada" },
};

const CATEGORY_LABELS = {
  MARKETING: "Marketing",
  UTILITY: "Utilidad",
  AUTHENTICATION: "Autenticación",
};

const StatusChip = ({ status }) => {
  const key = String(status || "").toUpperCase();
  const s = STATUS_STYLE[key] || { color: "#616161", background: "#f5f5f5", label: status };
  const sendable = key === "APPROVED";
  return (
    <Tooltip
      title={
        sendable
          ? "Aprobada por Meta: se puede enviar a clientes."
          : "No se puede enviar hasta que Meta la apruebe."
      }
    >
      <Chip
        size="small"
        label={s.label}
        style={{ color: s.color, backgroundColor: s.background, fontWeight: 600 }}
      />
    </Tooltip>
  );
};

export default function WabaTemplates() {
  const classes = useStyles();

  const {
    templates,
    loading,
    syncing,
    searchParam,
    statusFilter,
    whatsappFilter,
    wabaConnections,
    STATUS_OPTIONS,
    fetchTemplates,
    handleSearch,
    handleStatusFilter,
    handleWhatsappFilter,
    handleSync,
    handleDelete,
  } = useWabaTemplatesPage();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mapa conexión → nombre para la columna "Conexión".
  const connectionNameById = useMemo(() => {
    const map = {};
    wabaConnections.forEach((w) => {
      map[w.id] = w.name;
    });
    return map;
  }, [wabaConnections]);

  const hasWaba = wabaConnections.length > 0;

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <DescriptionOutlinedIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          Plantillas Meta
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={!hasWaba}
            style={{ marginRight: 8 }}
          >
            Crear plantilla
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing || !hasWaba}
          >
            {syncing ? "Sincronizando..." : "Sincronizar desde Meta"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Barra de búsqueda y filtros */}
        <div className={classes.searchRow}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nombre..."
            value={searchParam}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel>Conexión</InputLabel>
            <Select
              label="Conexión"
              value={whatsappFilter}
              onChange={(e) => handleWhatsappFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {wabaConnections.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {hasWaba && (
          <Typography variant="caption" className={classes.helpText}>
            Las plantillas se crean y aprueban en <strong>Meta (WhatsApp Manager)</strong>.
            Aquí las sincronizas; solo las <strong>Aprobadas</strong> pueden enviarse a
            clientes fuera de la ventana de 24 h.
          </Typography>
        )}

        {/* Aviso: sin conexiones WABA no hay plantillas que sincronizar */}
        {!hasWaba && !loading && (
          <div className={classes.emptyBox}>
            <InfoOutlinedIcon fontSize="large" />
            <Typography>
              No hay conexiones de WhatsApp Cloud API (Meta). Crea una conexión WABA
              para sincronizar sus plantillas.
            </Typography>
          </div>
        )}

        {/* Tabla */}
        {hasWaba && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Idioma</TableCell>
                <TableCell>Conexión</TableCell>
                <TableCell>Vista previa</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={7} />
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary" style={{ padding: 16 }}>
                      No hay plantillas sincronizadas. Usa "Sincronizar desde Meta".
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell><strong>{t.name}</strong></TableCell>
                    <TableCell align="center"><StatusChip status={t.status} /></TableCell>
                    <TableCell>{CATEGORY_LABELS[t.category] || t.category || "—"}</TableCell>
                    <TableCell>{t.language}</TableCell>
                    <TableCell>{connectionNameById[t.whatsappId] || `#${t.whatsappId ?? "—"}`}</TableCell>
                    <TableCell>
                      <Tooltip title={getTemplateBodyText(t.components)}>
                        <span className={classes.bodyCell}>
                          {getTemplateBodyText(t.components) || "—"}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Eliminar plantilla">
                        <IconButton size="small" onClick={() => setDeleteTarget(t)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <WabaTemplateCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        wabaConnections={wabaConnections}
        onCreated={() => fetchTemplates(searchParam, statusFilter, whatsappFilter)}
      />

      <ConfirmationModal
        title={`¿Eliminar la plantilla "${deleteTarget?.name}"?`}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) handleDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
      >
        Se eliminará en Meta y en el CRM. Esta acción no se puede deshacer.
      </ConfirmationModal>
    </MainContainer>
  );
}
